import {
  FC,
  forwardRef,
  ForwardedRef,
  HTMLAttributes,
  KeyboardEvent,
  useContext,
  ReactNode,
  MouseEvent,
} from "react";
import { ThemeContext } from "styled-components";
import { LayoutProps } from "styled-system";

import Box from "../Box/Box";
import { BoxProps } from "../Box/box.type";
import Icon, { IconWeight } from "../Icon/Icon";
import Typography from "../Typography/Typography";

export interface ChipProps
  extends Omit<HTMLAttributes<HTMLElement>, "color" | "css">,
    BoxProps {
  label: string;
  onRemove?: () => void;
  onClick?: () => void;
  disabled?: boolean;
  readOnly?: boolean;
  icon?: string;
  iconWeight?: IconWeight;
  iconWidth?: LayoutProps["width"];
  iconHeight?: LayoutProps["height"];
  iconColor?: string;
}

const Chip: FC<ChipProps> = forwardRef(
  (
    {
      label,
      disabled = false,
      readOnly = false,
      onClick,
      onRemove = () => {},
      icon,
      iconWeight = "regular",
      iconWidth = "14px",
      iconHeight = "14px",
      iconColor,
      ...props
    },
    ref: ForwardedRef<ReactNode>
  ) => {
    const themeContext = useContext(ThemeContext);
    const chipBorderColor = readOnly
      ? themeContext.colors["chip-read-only-border-color"]
      : themeContext.colors["cta-button-border-color-hover"];

    const iconDefaultColor = iconColor
      ? iconColor
      : readOnly
      ? "msg-blue-border"
      : "primary-light-blue";

    const chipCss = `
    border: 2px solid;
    border-radius: 48px;
    border-color: ${chipBorderColor};
    opacity: ${!readOnly && disabled ? 0.3 : 1};
    > div:last-child:not([aria-disabled="true"]) {
        cursor: pointer;
    }
    span:first-child {
        cursor: default;
    }
    &:hover:not([aria-disabled="true"]), &:focus:not([aria-disabled="true"]){
      box-shadow: ${
        !readOnly
          ? `0px 1px 2px 2px rgba(21, 45, 109, 0.08),
             2px 0px 8px 4px rgba(161, 194, 218, 0.24),
             1px 1px 2px 2px rgba(21, 45, 109, 0.06),
             2px 2px 4px 2px rgba(161, 194, 218, 0.06),
             0px 2px 8px 2px rgba(161, 194, 218, 0.06)`
          : "none"
      }
    }
  `;

    let onKeyDown = (e: KeyboardEvent<HTMLImageElement>) => {
      if (
        !(disabled || readOnly) &&
        (e.key === "Delete" || e.key === "Backspace")
      ) {
        onRemove();
      }
    };

    const handleRemoveClick = (e: MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();

      onRemove();
    };

    return (
      <Box
        {...props}
        tabIndex={!(readOnly || disabled) ? 0 : -1}
        display="flex"
        width="fit-content"
        alignItems="center"
        justifyContent="center"
        px={4}
        py={3}
        aria-disabled={disabled}
        aria-label={label}
        css={chipCss}
        onClick={onClick}
        onKeyDown={onKeyDown}
        ref={ref}
      >
        {icon && (
          <Icon
            name={icon}
            weight={iconWeight}
            width={iconWidth}
            height={iconHeight}
            color={iconDefaultColor}
            marginRight="12px"
          />
        )}
        <Typography
          variant="body-small"
          m={0}
          mr={readOnly ? 0 : 2}
          htmlTag="span"
          fontWeight="600"
          css={`
            white-space: nowrap;
            user-select: none;
          `}
          color={readOnly ? "lighter-text-color" : "primary-navy-blue"}
        >
          {label}
        </Typography>
        {!readOnly && (
          <Icon
            role="button"
            name="times"
            weight="solid"
            width="18px"
            height="18px"
            color="cta-primary-background-color-default"
            onClick={!disabled ? handleRemoveClick : undefined}
            aria-disabled={disabled}
            aria-label={`Remove ${label}`}
          />
        )}
      </Box>
    );
  }
);

export default Chip;
